/** Stylus's `transcript.final.json` (v1), and the views the playground draws from it. */

import {
  categoryOf,
  entityLabel,
  humanize,
  type Category,
  type Extraction,
  type TimeSpan,
} from './extraction';

export interface TranscriptWord {
  text: string;
  start_ms: number;
  end_ms: number;
  confidence?: number | null;
}

export interface TranscriptSegment {
  id: string;
  track_id: string;
  speaker?: string | null;
  start_ms: number;
  end_ms: number;
  text: string;
  words?: TranscriptWord[];
}

export interface Transcript {
  schema_version?: number;
  duration_ms: number;
  locale?: string;
  tracks?: { id: string; label: string }[];
  segments: TranscriptSegment[];
  engine?: { timing?: string };
}

/** "1:04", or "1:02:09" past the hour; tenths when asked, for a slice's bounds. */
export function formatClock(ms: number, { tenths = false } = {}): string {
  const safe = Math.max(0, ms);
  const total = Math.floor(safe / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const ss = String(seconds).padStart(2, '0');
  const fraction = tenths ? `.${Math.floor((safe % 1000) / 100)}` : '';
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${ss}${fraction}`
    : `${minutes}:${ss}${fraction}`;
}

/** "2m 30s", "45s", "1h 5m": how long a recording is, for a list. */
export function formatDuration(ms: number): string {
  const total = Math.round(ms / 1000);
  if (total < 60) {
    return `${total}s`;
  }
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m ${total % 60}s`;
}

/**
 * A segment's timed words. Without word timings the text is spread over the
 * segment by length, so highlighting still moves, at segment accuracy.
 */
export function wordsOf(segment: TranscriptSegment): TranscriptWord[] {
  if (segment.words && segment.words.length > 0) {
    return segment.words;
  }
  const parts = segment.text.split(/\s+/).filter(Boolean);
  const letters = parts.reduce((sum, part) => sum + part.length, 0) || 1;
  const span = Math.max(0, segment.end_ms - segment.start_ms);
  let cursor = segment.start_ms;
  return parts.map((text) => {
    const start = cursor;
    cursor += (span * text.length) / letters;
    return { text, start_ms: Math.round(start), end_ms: Math.round(cursor) };
  });
}

export function speakerKey(trackId: string, label: string | null | undefined): string {
  return `${trackId}:${label ?? ''}`;
}

export interface Speaker {
  key: string;
  trackId: string;
  label: string | null;
  /** "Ada Byrne" when the speaker said their name, else "Speaker 1". */
  name: string;
  /** "Applicant", "Advisor"; declared by the caller, else inferred from the speech. */
  role: string | null;
  entityId: string | null;
  /** 1-based slot into the categorical series, for the speaker's colour. */
  slot: number;
  talkTimeMs: number;
}

const SERIES_SLOTS = 8;

/** Every speaker in the transcript, in order of first speech, named from the extraction. */
export function speakersOf(transcript: Transcript, extraction: Extraction | null): Speaker[] {
  const provenance = new Map(
    (extraction?.transcript?.speakers ?? []).map((speaker) => [
      speakerKey(speaker.track_id, speaker.label),
      speaker,
    ]),
  );
  const multiTrack = new Set(transcript.segments.map((segment) => segment.track_id)).size > 1;
  const trackLabel = new Map((transcript.tracks ?? []).map((track) => [track.id, track.label]));
  const found = new Map<string, Speaker>();
  for (const segment of [...transcript.segments].sort((a, b) => a.start_ms - b.start_ms)) {
    const key = speakerKey(segment.track_id, segment.speaker);
    const talk = Math.max(0, segment.end_ms - segment.start_ms);
    const existing = found.get(key);
    if (existing) {
      existing.talkTimeMs += talk;
      continue;
    }
    const known = provenance.get(key);
    const entity = extraction?.entities?.find((candidate) => candidate.id === known?.entity_id);
    const named = entity ? entityLabel(entity) : null;
    const index = found.size + 1;
    const fallback = segment.speaker
      ? `Speaker ${segment.speaker.replace(/^S/i, '')}`
      : `Speaker ${index}`;
    const role = known?.declared_role ?? known?.inferred_role ?? null;
    found.set(key, {
      key,
      trackId: segment.track_id,
      label: segment.speaker ?? null,
      name:
        named && named !== humanize('person')
          ? named
          : multiTrack && trackLabel.get(segment.track_id)
            ? `${fallback} · ${trackLabel.get(segment.track_id)}`
            : fallback,
      role: role ? humanize(role) : null,
      entityId: known?.entity_id ?? null,
      slot: ((index - 1) % SERIES_SLOTS) + 1,
      talkTimeMs: talk,
    });
  }
  return [...found.values()];
}

/** A word that some extracted value was grounded on. */
export interface WordMark {
  entityId: string;
  field: string;
  category: Category;
  label: string;
}

/** Word marks by `segmentId:wordIndex`, from every located field in the extraction. */
export function marksOf(
  transcript: Transcript,
  extraction: Extraction | null,
): Map<string, WordMark[]> {
  const marks = new Map<string, WordMark[]>();
  const segments = transcript.segments;
  for (const entity of extraction?.entities ?? []) {
    if (entity.discarded) {
      continue;
    }
    for (const [field, value] of Object.entries(entity.fields ?? {})) {
      const spans = [value.span, ...(value.occurrences ?? []).map((o) => o.span)].filter(
        (span): span is TimeSpan => Boolean(span),
      );
      const mark: WordMark = {
        entityId: entity.id,
        field,
        category: categoryOf(entity.type),
        label: `${humanize(field)}: ${value.corrected_value || value.value}`,
      };
      for (const span of spans) {
        for (const segment of segmentsIn(segments, span)) {
          wordsOf(segment).forEach((word, index) => {
            if (inSpan(word, span)) {
              const key = `${segment.id}:${index}`;
              const list = marks.get(key) ?? [];
              if (!list.some((m) => m.entityId === mark.entityId && m.field === mark.field)) {
                list.push(mark);
              }
              marks.set(key, list);
            }
          });
        }
      }
    }
  }
  return marks;
}

const TOLERANCE_MS = 40;

export function inSpan(word: TranscriptWord, span: Pick<TimeSpan, 'start_ms' | 'end_ms'>): boolean {
  return word.start_ms >= span.start_ms - TOLERANCE_MS && word.end_ms <= span.end_ms + TOLERANCE_MS;
}

/** The segments a span was read from: by id when it names them, else by overlap on its track. */
export function segmentsIn(segments: TranscriptSegment[], span: TimeSpan): TranscriptSegment[] {
  const ids = new Set(span.segment_ids ?? []);
  if (ids.size > 0) {
    const named = segments.filter((segment) => ids.has(segment.id));
    if (named.length > 0) {
      return named;
    }
  }
  return segments.filter(
    (segment) =>
      segment.track_id === span.track_id &&
      segment.start_ms < span.end_ms &&
      segment.end_ms > span.start_ms,
  );
}

export interface SliceLine {
  segment: TranscriptSegment;
  words: { text: string; start_ms: number; end_ms: number; inSpan: boolean }[];
  /** Words were cut from the start or the end of the turn, to keep it about the span. */
  clippedStart: boolean;
  clippedEnd: boolean;
  /** A neighbour shown for context, not a segment the value was read from. */
  context: boolean;
}

/** A span with its whole segments around it, and one neighbouring turn either side. */
/** Words of a neighbouring turn kept for context, nearest the span. */
const CONTEXT_WORDS = 14;
/** Words kept either side of the span in the turn it was said in. */
const AROUND_WORDS = 20;

function clip(line: SliceLine, from: number, to: number): SliceLine {
  const start = Math.max(0, from);
  const end = Math.min(line.words.length, to);
  return {
    ...line,
    words: line.words.slice(start, end),
    clippedStart: start > 0,
    clippedEnd: end < line.words.length,
  };
}

/**
 * A span with the turns it was said in, trimmed to the words around it, and
 * the end of the turn before and the start of the turn after, for context.
 */
export function sliceOf(transcript: Transcript, span: TimeSpan): SliceLine[] {
  const ordered = [...transcript.segments].sort((a, b) => a.start_ms - b.start_ms);
  const inside = segmentsIn(ordered, span);
  if (inside.length === 0) {
    return [];
  }
  const first = ordered.indexOf(inside[0]);
  const last = ordered.indexOf(inside[inside.length - 1]);
  const line = (segment: TranscriptSegment, context: boolean): SliceLine => ({
    segment,
    context,
    clippedStart: false,
    clippedEnd: false,
    words: wordsOf(segment).map((word) => ({
      ...word,
      inSpan: !context && segment.track_id === span.track_id && inSpan(word, span),
    })),
  });
  const around = (full: SliceLine): SliceLine => {
    const hits = full.words.flatMap((word, index) => (word.inSpan ? [index] : []));
    return hits.length === 0
      ? clip(full, 0, AROUND_WORDS * 2)
      : clip(full, hits[0] - AROUND_WORDS, hits[hits.length - 1] + 1 + AROUND_WORDS);
  };
  const before = first > 0 ? line(ordered[first - 1], true) : null;
  const after = last < ordered.length - 1 ? line(ordered[last + 1], true) : null;
  return [
    ...(before ? [clip(before, before.words.length - CONTEXT_WORDS, before.words.length)] : []),
    ...ordered
      .slice(first, last + 1)
      .map((segment) => around(line(segment, !inside.includes(segment)))),
    ...(after ? [clip(after, 0, CONTEXT_WORDS)] : []),
  ];
}

/** Index of the word being spoken at `ms`, or of the last one before it; -1 before the first. */
export function wordAt(words: Pick<TranscriptWord, 'start_ms'>[], ms: number): number {
  let low = 0;
  let high = words.length - 1;
  let found = -1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    if (words[mid].start_ms <= ms) {
      found = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return found;
}
